import React, { Dispatch, SetStateAction, useEffect } from "react";
//@ts-ignore
import imagedevice from "src/assets/images/render1.png";
import { FaCheck, FaPlus, FaMinus } from "react-icons/fa6";
import { GoArrowRight } from "react-icons/go";
import { HiOutlineInformationCircle } from "react-icons/hi2";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { itemsAtom, numberWizards } from "src/atoms/constantsCheckout";

interface Props {
    step: number;
    setStep: Dispatch<SetStateAction<number>>;
    packages: any[];
}

export const CameraStep = ({ step, setStep, packages }: Props) => {
    const [items, setItems] = useAtom(itemsAtom);
    const [number, setNumber] = useAtom(numberWizards);
    const wizardPackage = packages.find((pkg) => pkg.id === "1");
    const wizardPrice = wizardPackage
        ? parseFloat(wizardPackage.price)
        : 100 * 10;

    const { t } = useTranslation();

    useEffect(() => {
        setNumber(items.wizards.quantity ?? 1);
    }, [items.wizards.quantity, setNumber]);

    const handleQuantityChange = (newQuantity: number) => {
        if (newQuantity >= 0) {
            setNumber(newQuantity);
        }
    };

    const continueButtonClass =
        "w-full group relative bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-4 rounded-2xl font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-3";

    return (
        <div className="min-h-screen bg-gray-50/50">
            <div className="max-w-6xl mx-auto px-6 py-8">
                {/* Header Section */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-3">
                        {t("cameraStep.title")}
                    </h1>
                    <p className="text-lg text-gray-600 leading-relaxed">
                        {t("cameraStep.description")}
                    </p>
                </div>

                {/* Main Content Grid */}
                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Product Card - Takes 2 columns */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300">
                            {/* Product Header */}
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-8 py-6 border-b border-gray-100">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                                            {t("cameraStep.device.name")}
                                        </h3>
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-3xl font-bold text-blue-600">
                                                {wizardPrice}€
                                            </span>
                                            <span className="text-gray-500 text-sm">
                                                {t("cameraStep.device.priceSuffix")}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Product Content */}
                            <div className="p-8">
                                <div className="grid md:grid-cols-2 gap-8 items-center">
                                    {/* Product Image */}
                                    <div className="relative">
                                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 text-center">
                                            <img
                                                alt="Wizard Camera Device"
                                                src={imagedevice}
                                                className="w-full max-w-xs mx-auto h-auto object-contain drop-shadow-2xl hover:scale-105 transition-transform duration-300"
                                            />
                                        </div>
                                    </div>

                                    {/* Features List */}
                                    <div className="space-y-6">
                                        <h4 className="text-xl font-semibold text-gray-900 mb-4">
                                            {t("cameraStep.characteristics_includes")}
                                        </h4>
                                        <div className="space-y-4">
                                            {[
                                                {
                                                    key: "camera",
                                                    label: t("cameraStep.device.features.camera"),
                                                },
                                                {
                                                    key: "lan",
                                                    label: t("cameraStep.device.features.lan"),
                                                },
                                                {
                                                    key: "suitcase",
                                                    label: t("cameraStep.device.features.suitcase"),
                                                },
                                                {
                                                    key: "charger",
                                                    label: t("cameraStep.device.features.charger"),
                                                },
                                            ].map((feature, index) => (
                                                <div
                                                    key={feature.key}
                                                    className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-blue-50 transition-colors duration-200"
                                                    style={{ animationDelay: `${index * 100}ms` }}
                                                >
                                                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                                        <FaCheck className="text-blue-600 w-4 h-4" />
                                                    </div>
                                                    <span className="text-gray-700 font-medium flex-1">
                                                        {t(`cameraStep.device.features.${feature.key}`) ||
                                                            feature.label}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Quantity & Summary Card */}
                    <div className="space-y-6">
                        {/* Quantity Selector */}
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4">
                                {t("cameraStep.summary.quantity")}
                            </h4>

                            <div className="flex items-center justify-center gap-4 mb-6">
                                <button
                                    onClick={() => handleQuantityChange(number - 1)}
                                    disabled={number <= 1}
                                    className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors duration-200"
                                >
                                    <FaMinus className="w-4 h-4 text-gray-600" />
                                </button>

                                <div className="w-16 h-16 rounded-2xl bg-blue-50 border-2 border-blue-200 flex items-center justify-center">
                                    <span className="text-2xl font-bold text-blue-600">
                                        {number}
                                    </span>
                                </div>

                                <button
                                    onClick={() => handleQuantityChange(number + 1)}
                                    className="w-10 h-10 rounded-full bg-blue-100 hover:bg-blue-200 flex items-center justify-center transition-colors duration-200"
                                >
                                    <FaPlus className="w-4 h-4 text-blue-600" />
                                </button>
                            </div>

                            <div className="text-center text-sm text-gray-600">
                                {t("cameraStep.selected_devices")}
                            </div>
                        </div>

                        {/* Price Summary */}
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4">
                                {t("cameraStep.summary.title")}
                            </h4>

                            <div className="space-y-3">
                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                    <span className="text-gray-600">
                                        {" "}
                                        {t("cameraStep.summary.unitPrice")}
                                    </span>
                                    <span className="font-semibold text-gray-900">
                                        {wizardPrice}€
                                    </span>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                    <span className="text-gray-600">
                                        {" "}
                                        {t("cameraStep.summary.quantity")}
                                    </span>
                                    <span className="font-semibold text-gray-900">×{number}</span>
                                </div>
                                <div className="flex justify-between items-center py-3">
                                    <span className="text-lg font-semibold text-gray-900">
                                        {t("cameraStep.summary.total")}
                                    </span>
                                    <span className="text-2xl font-bold text-blue-600">
                                        {(wizardPrice * number).toFixed(0)}€
                                    </span>
                                </div>
                            </div>

                            <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
                                <div className="flex items-start gap-3">
                                    <HiOutlineInformationCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                                    <div className="text-sm">
                                        <p className="text-amber-800 font-medium mb-1">
                                            {t("cameraStep.shipping.title")}
                                        </p>
                                        <p className="text-amber-700">
                                            {t("cameraStep.shipping.description")}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Navigation Buttons */}
                        <div className="space-y-3">
                            {step > 1 && (
                                <button
                                    onClick={() => setStep(step - 1)}
                                    className="w-full px-8 py-3 border border-gray-300 rounded-2xl font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    {t("subscriptionView.buttons.goBack")}
                                </button>
                            )}
                            <button
                                onClick={() => {
                                    setStep(step + 1);
                                    setItems((prev: any) => ({
                                        ...prev,
                                        wizards: {
                                            type: t("cameraStep.device.features.camera"),
                                            quantity: number,
                                            price: wizardPrice,
                                            totalPrice: number * wizardPrice,
                                            price_id: "price_1SewZpCuyJlytLHzgP91QEVC"
                                        },
                                    }));
                                }}
                                className={continueButtonClass}
                            >
                                <span>{t("cameraStep.continue") || "Continuar"}</span>
                                <GoArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />

                                {/* Button glow effect */}
                                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-300"></div>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Trust Indicators */}
                <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
                    {[
                        {
                            title: t("cameraStep.trustIndicators.securePayment.title"),
                            desc: t("cameraStep.trustIndicators.securePayment.desc"),
                        },
                        {
                            title: t("cameraStep.trustIndicators.fastShipping.title"),
                            desc: t("cameraStep.trustIndicators.fastShipping.desc"),
                        },
                        {
                            title: t("cameraStep.trustIndicators.easyReturn.title"),
                            desc: t("cameraStep.trustIndicators.easyReturn.desc"),
                        },
                        {
                            title: t("cameraStep.trustIndicators.support.title"),
                            desc: t("cameraStep.trustIndicators.support.desc"),
                        },
                    ].map((item, index) => (
                        <div
                            key={index}
                            className="text-center p-4 bg-white rounded-xl shadow-sm border border-gray-100"
                        >
                            <h5 className="font-semibold text-gray-900 text-sm">
                                {item.title}
                            </h5>
                            <p className="text-xs text-gray-600 mt-1">{item.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
